import { isEmpty } from "lodash-es"
import { Box } from "@mui/material"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { useDateTime } from "../../../../../modules/localization/useDateTime"

import { BookingConfig, DynamicConfigKey, DynamicMainFormValues } from "../../../createOrder/components/form/formTypes"
import { TimelineCalendarEventTimeRange } from "../../../createOrder/components/resource/calendar/fullCalendarTypes"
import { ResourceItem, SelectedMainUserState } from "../../../createOrder/createOrderTypes"
import { ShareOutlookAttendeesToggle } from "../../../createOrder/components/ShareOutlookAttendeesToggle"
import { FormikSubmitButton } from "../../../forms/Formik"
import { MeetingDetails } from "./MeetingDetails"
import { ResourcesInfo } from "./ResourcesInfo"
import { DEFAULT_MINIMUM_DURATION_MINUTES } from "../../../createOrder/components/resource/resourceBookingCalendars/helpers"

const useStyles = makeStyles()(() => ({
    container: {
        display: "grid",
        gridTemplateColumns: "5fr 4fr",
        gap: "3.5rem",
        width: "100%",
        maxWidth: "60rem",
    },
    outlookToggleWrap: {
        width: "100%",
        maxWidth: "60rem",
    },
    buttonWrap: {
        textAlign: "center",
        paddingTop: "1rem",
    },
    button: {
        minWidth: "15rem",
    },
}))

type MeetingDetailsFormPartProps = Readonly<{
    config: BookingConfig
    handleClick: (isShowParticipants: boolean) => void
    resourcesList: ResourceItem[]
    timeRange?: TimelineCalendarEventTimeRange
    handleClearResourceIds: () => void
    user: SelectedMainUserState
    isLockedForOtherUser?: boolean
}>

export function MeetingDetailsFormPart({
    config,
    handleClick,
    resourcesList,
    timeRange,
    handleClearResourceIds,
    user,
    isLockedForOtherUser,
}: MeetingDetailsFormPartProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    const { values, submitForm, validateForm } = useFormikContext<DynamicMainFormValues>()

    // const { resources: matchingResources } = findResourcesWithMessaging(values["resourceIds"], resourcesList || [])
    const matchingResources =
        resourcesList.filter((resource) => values["resourceIds"].includes(resource.id.toString())) || []

    const minTime = timeRange ? dateTimeFactory.fromISO(timeRange.start) : null
    const maxTime = timeRange ? dateTimeFactory.fromISO(timeRange.end) : null

    const startTime = dateTimeFactory.fromISO(values["startDateTime"])
    const endTime = dateTimeFactory.fromISO(values["endDateTime"])

    const isTimeInvalid = timeRange
        ? startTime < minTime! ||
          startTime >= maxTime! ||
          endTime <= startTime.plus({ minutes: DEFAULT_MINIMUM_DURATION_MINUTES }) ||
          endTime > maxTime!
        : false

    const handleConfirmFirstStep = async () => {
        // Validate fresh instead of reading the errors snapshot: on the first click nothing has
        // been validated yet, so the snapshot is empty even for invalid values. The participants
        // form is excluded because it is filled on step 2, after this navigation.
        const validationErrors = await validateForm()

        const hasBlockingErrors = Object.entries(validationErrors).some(([key, value]) => {
            return key !== DynamicConfigKey.ADD_PARTICIPANTS_FORM && !!value && !isEmpty(value)
        })

        if (hasBlockingErrors) {
            submitForm() // increments submitCount so field errors become visible
            return
        }

        handleClick(values[DynamicConfigKey.ADD_PARTICIPANTS_TOGGLE] === true)
    }

    return (
        <>
            <Box className={classes.container}>
                <MeetingDetails
                    resourcesList={matchingResources}
                    timeRange={timeRange}
                    user={user}
                    config={config}
                    isLockedForOtherUser={isLockedForOtherUser}
                />
                <ResourcesInfo
                    config={config}
                    user={user}
                    resourcesList={matchingResources}
                    // hasMessagingEnabled={hasMessagingEnabled}
                    handleClearResourceIds={handleClearResourceIds}
                />
            </Box>
            <Box className={classes.outlookToggleWrap}>
                <ShareOutlookAttendeesToggle
                    fieldKey={DynamicConfigKey.ADD_PARTICIPANTS_FORM}
                    disabled={isLockedForOtherUser}
                />
            </Box>
            <Box className={classes.buttonWrap}>
                <LocalizedStrict id="create-order-confirm-booking-dialog-confirm-button">
                    <FormikSubmitButton
                        fullWidth={false}
                        updateDefaultClasses={classes.button}
                        onClick={(event) => {
                            // The button is type="submit"; without this the native form submission
                            // would fire a second, parallel submit alongside handleConfirmFirstStep.
                            event.preventDefault()
                            handleConfirmFirstStep()
                        }}
                        parentDisabled={isTimeInvalid}
                    >
                        Confirm
                    </FormikSubmitButton>
                </LocalizedStrict>
            </Box>
        </>
    )
}
